import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import * as Sentry from '@sentry/react'
import { useSession } from '@/auth/useSession'
import {
  shouldAttemptSigninRedirect,
  recordSigninRedirect,
  clearSigninRedirect,
} from '@/auth/redirectLoopGuard'
import { apiGet, ApiError, TimeoutError, UnauthorizedError } from './client'
import type { Account } from './types'

const ACCOUNTS_PATH = '/applications/lobby/me/accounts'
const STALE_TIME_MS = 60_000

/**
 * TanStack Query hook fetching the signed-in user's workspaces.
 *
 * - Gated on `accessToken` — nothing fires until the OIDC handshake
 *   has produced a usable bearer.
 * - Keyed on `sub` so switching accounts (rare, but e.g. after sign-out
 *   + sign-in as a different user) doesn't bleed one user's cache into
 *   another's view.
 * - No automatic retries: a failed fetch surfaces the error card (with
 *   its manual Retry button) immediately instead of silently re-hitting
 *   a backend that already told us no. `apiGet`'s own timeout bounds
 *   how long "no answer" can take.
 * - On 401 we trigger `signinRedirect()` — `automaticSilentRenew` will
 *   have already attempted refresh, so a 401 here means the session is
 *   dead. Two guards bound it: a ref dedupes re-renders while the same
 *   error instance sticks around, and `redirectLoopGuard` (sessionStorage,
 *   survives the full-page redirect) allows at most one redirect per
 *   minute — a 401 arriving right after a successful Passport round trip
 *   means re-auth can't fix it, so we show the error card instead of
 *   looping.
 * - Non-401 errors (network, timeout, 5xx) surface via `query.error` for
 *   the caller's error UI AND get forwarded to Sentry with useful tags so
 *   incidents are actionable from the dashboard.
 */
export function useAccounts() {
  const { sub, accessToken } = useSession()
  const auth = useAuth()
  const reportedRef = useRef<Error | null>(null)
  const redirectedRef = useRef<Error | null>(null)

  const query = useQuery<Account[], Error>({
    queryKey: ['accounts', sub],
    queryFn: () => apiGet<Account[]>(ACCOUNTS_PATH, accessToken),
    enabled: !!accessToken,
    staleTime: STALE_TIME_MS,
    retry: false,
  })

  useEffect(() => {
    // A successful authorized fetch proves the session works — re-arm
    // the loop guard so the next genuine expiry gets its one redirect.
    if (query.isSuccess) clearSigninRedirect()
  }, [query.isSuccess])

  useEffect(() => {
    const err = query.error
    if (!err) {
      reportedRef.current = null
      redirectedRef.current = null
      return
    }

    if (err instanceof UnauthorizedError) {
      // Only handle each error instance once — TanStack keeps `error`
      // stable across re-renders until the query resolves or refetches,
      // so without this guard we'd re-run on every render tick.
      if (redirectedRef.current === err) return
      redirectedRef.current = err
      if (shouldAttemptSigninRedirect()) {
        recordSigninRedirect()
        void auth.signinRedirect()
        return
      }
      // We already went through Passport within the last minute and the
      // API still says 401 — another redirect can't fix it and would
      // loop (full page reload each round, so refs don't help). Report
      // and fall through to the error card.
      Sentry.captureException(err, {
        tags: { endpoint: 'accounts', status: '401-loop' },
      })
      return
    }

    // Deduplicate Sentry reports per error instance for the same reason.
    if (reportedRef.current === err) return
    reportedRef.current = err
    Sentry.captureException(err, {
      tags: {
        endpoint: 'accounts',
        status:
          err instanceof ApiError
            ? String(err.status)
            : err instanceof TimeoutError
              ? 'timeout'
              : 'network',
      },
    })
  }, [query.error, auth])

  return query
}
